'use client';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { AppDispatch } from '@/app/store';
import {
  acceptAll,
  rejectAll,
  savePreferences,
  closePreferences,
  selectCookieConsent,
  selectShowPreferences,
} from '@/app/store/slices/cookieConsentSlice';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Settings2, BarChart3, Megaphone } from 'lucide-react';

// ─── Category row ─────────────────────────────────────────────────────────────

interface CategoryRowProps {
  icon: React.ReactNode;
  name: string;
  description: string;
  enabled: boolean;
  alwaysOn?: boolean;
  alwaysOnLabel?: string;
  onToggle?: (v: boolean) => void;
}

const CategoryRow = React.memo(({
  icon,
  name,
  description,
  enabled,
  alwaysOn,
  alwaysOnLabel,
  onToggle,
}: CategoryRowProps) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-border last:border-0">
    <div className="flex items-start gap-3 min-w-0">
      <div className="mt-0.5 text-muted-foreground shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">{name}</span>
          {alwaysOn && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {alwaysOnLabel}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
    <div className="shrink-0 mt-0.5">
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        disabled={alwaysOn}
        aria-label={name}
      />
    </div>
  </div>
));
CategoryRow.displayName = 'CategoryRow';

// ─── CookiePreferencesModal ───────────────────────────────────────────────────

export const CookiePreferencesModal = React.memo(() => {
  const t = useTranslations('cookieConsent.preferences');
  const dispatch = useDispatch<AppDispatch>();
  const open = useSelector(selectShowPreferences);
  const consent = useSelector(selectCookieConsent);

  const [functional, setFunctional] = useState(consent.functional);
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);

  // Sync local state when modal opens
  useEffect(() => {
    if (open) {
      setFunctional(consent.functional);
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [open, consent.functional, consent.analytics, consent.marketing]);

  const handleSave = () => {
    dispatch(savePreferences({ functional, analytics, marketing }));
  };

  const handleAcceptAll = () => {
    dispatch(acceptAll());
  };

  const handleRejectAll = () => {
    dispatch(rejectAll());
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && dispatch(closePreferences())}>
      <DialogContent className="w-[95vw] max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{t('title')}</DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {/* Essential */}
          <CategoryRow
            icon={<ShieldCheck className="h-4 w-4" />}
            name={t('categories.essential.name')}
            description={t('categories.essential.description')}
            enabled={true}
            alwaysOn
            alwaysOnLabel={t('categories.essential.alwaysOn')}
          />

          {/* Functional */}
          <CategoryRow
            icon={<Settings2 className="h-4 w-4" />}
            name={t('categories.functional.name')}
            description={t('categories.functional.description')}
            enabled={functional}
            onToggle={setFunctional}
          />

          {/* Analytics */}
          <CategoryRow
            icon={<BarChart3 className="h-4 w-4" />}
            name={t('categories.analytics.name')}
            description={t('categories.analytics.description')}
            enabled={analytics}
            onToggle={setAnalytics}
          />

          {/* Marketing */}
          <CategoryRow
            icon={<Megaphone className="h-4 w-4" />}
            name={t('categories.marketing.name')}
            description={t('categories.marketing.description')}
            enabled={marketing}
            onToggle={setMarketing}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          <Button size="sm" onClick={handleSave} className="w-full">
            {t('save')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleAcceptAll}>
              {t('acceptAll')}
            </Button>
            <Button variant="ghost" size="sm" className="flex-1 text-xs text-muted-foreground" onClick={handleRejectAll}>
              {t('rejectAll')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});
CookiePreferencesModal.displayName = 'CookiePreferencesModal';
